/**
 * Trace Studio panel — My Content tab: team folders with asset upload
 * (drag-drop or button), import/open, delete, and scene publishing.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Teams, Folders, Assets, EntityType } from 'trace-viewer/cms';
import { withContentStorage } from '../cms/asset-writes';
import { IS_FOLDER_STORAGE } from '../config/contentStorage';
import { publishErrorMessage, nameMismatchDetail } from '../cms/publish-errors';
import { publishBundleAsAssetFolder, publishNewAssetFolder } from '../cms/create-asset-folder';
import type { FolderTeamResponseDto } from 'trace-viewer/cms';
import {
    styles,
    apiConfig,
    doImportAsset,
    doOpenAsset,
    sceneHasContent,
    ConfirmOpenBar,
    ConfirmDeleteBar,
    toAssetItem,
    AssetCard,
    AssetInfoBar,
    FolderCard,
    NewFolderCard,
    PublishCard,
    Spacers,
    getCatalogSelectedFolderId,
    setCatalogSelectedFolder,
    UPLOAD_EXTENSIONS,
    extensionToEntityType,
} from '../catalog-shared';
import type { AssetItem } from '../catalog-shared';
import { setCatalogStateSlot } from '../catalog-auth';
import { FilterSortBar, sortBy, type SortKey } from '../catalog-filters';
import { showSpinnerToast, showToast } from '../toast';
import { generateAssetThumbnail, captureSceneThumbnail, createPublishPreview, drawPixelsToCanvas, type ThumbnailImporter, documentRoot } from 'trace-viewer/editor';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { cachedSlot, useMirrorSlot, useSharedTeam, teamScopedSlot, useMirrorTeamScopedSlot, useCatalogRefresh } from './state';
import { legacyRootMetadata, renameDefaultDocumentRoot } from '../cms/document-root';
import { editorExclusion } from '../editor-content';

export const MyContentTab: React.FC<{ token: string; active: boolean }> = ({ token, active }) => {
    const [selectedTeamId] = useSharedTeam();
    const [folders, setFolders] = useState<FolderTeamResponseDto[]>(
        () => teamScopedSlot('myContent.folders', selectedTeamId, [] as FolderTeamResponseDto[]),
    );
    const [selectedFolder, setSelectedFolder_] = useState<FolderTeamResponseDto | null>(
        () => teamScopedSlot('myContent.selectedFolder', selectedTeamId, null as FolderTeamResponseDto | null),
    );
    const setSelectedFolder = useCallback((folder: FolderTeamResponseDto | null) => {
        setSelectedFolder_(folder);
        setCatalogSelectedFolder(folder?.folderId ?? null, folder?.title ?? null);
    }, []);
    const [assets, setAssets] = useState<AssetItem[]>(
        () => teamScopedSlot('myContent.assets', selectedTeamId, [] as AssetItem[]),
    );
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('ready');
    const [errorMsg, setErrorMsg] = useState('');
    const [insertingAssetId, setInsertingAssetId] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState<'import' | 'open' | false>(false);
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(
        () => teamScopedSlot('myContent.selectedAssetId', selectedTeamId, null as string | null),
    );
    // Search is split per navigation level so opening a folder and backing
    // out preserves the folder-list filter (and vice versa).
    const [search, setSearch] = useState<string>(
        () => teamScopedSlot('myContent.search', selectedTeamId, ''),
    );
    const [assetSearch, setAssetSearch] = useState<string>(
        () => teamScopedSlot('myContent.assetSearch', selectedTeamId, ''),
    );
    // Sort order is a user preference, not team data — plain (non-scoped) slot.
    const [sortKey, setSortKey] = useState<SortKey>(
        () => cachedSlot<SortKey>('myContent.sortKey', 'updated'),
    );
    useMirrorSlot('myContent.sortKey', sortKey);
    useMirrorTeamScopedSlot('myContent.folders', selectedTeamId, folders);
    useMirrorTeamScopedSlot('myContent.selectedFolder', selectedTeamId, selectedFolder);
    useMirrorTeamScopedSlot('myContent.assets', selectedTeamId, assets);
    useMirrorTeamScopedSlot('myContent.selectedAssetId', selectedTeamId, selectedAssetId);
    useMirrorTeamScopedSlot('myContent.search', selectedTeamId, search);
    useMirrorTeamScopedSlot('myContent.assetSearch', selectedTeamId, assetSearch);
    const [loadingAssets, setLoadingAssets] = useState(false);
    const teamsRef = useRef<Teams | null>(null);
    const foldersRef = useRef<Folders | null>(null);
    const [confirmingOpen, setConfirmingOpen] = useState(false);
    useEffect(() => { setConfirmingOpen(false); }, [selectedAssetId]);

    // Re-hydrate from the team-scoped cache whenever the active team changes
    // (the parent's team picker drives this). Initial state was already
    // seeded by the useState initializers above, so skip the very first run.
    // Team switches always land back on the folder listing: clear any open
    // folder (state + slots) rather than restoring one from the cache.
    const firstTeamHydrate = useRef(true);
    useEffect(() => {
        if (firstTeamHydrate.current) { firstTeamHydrate.current = false; return; }
        setFolders(teamScopedSlot('myContent.folders', selectedTeamId, [] as FolderTeamResponseDto[]));
        setSelectedFolder(null);
        setAssets([]);
        setSelectedAssetId(null);
        setSearch(teamScopedSlot('myContent.search', selectedTeamId, ''));
        setAssetSearch('');
        if (selectedTeamId) {
            // The mirror effects skip the team-change run, so clear the new
            // team's slots directly — the fetch effect below reads them.
            setCatalogStateSlot(`myContent.selectedFolder.${selectedTeamId}`, null);
            setCatalogStateSlot(`myContent.assets.${selectedTeamId}`, []);
            setCatalogStateSlot(`myContent.selectedAssetId.${selectedTeamId}`, null);
            setCatalogStateSlot(`myContent.assetSearch.${selectedTeamId}`, '');
        }
    }, [selectedTeamId, setSelectedFolder]);

    // Delete state
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleDeleteAsset = useCallback(async (asset: AssetItem) => {
        if (!asset.assetId || !selectedTeamId) return;
        setDeleting(true);
        const dismiss = showSpinnerToast(`Deleting "${asset.title ?? 'asset'}"...`);
        try {
            const assetsClient = new Assets(apiConfig(token));
            await assetsClient.assetsDelete(asset.assetId, { teamId: selectedTeamId });
            dismiss(`Deleted "${asset.title ?? 'asset'}"`, 'success');
            setAssets(prev => prev.filter(a => a.assetId !== asset.assetId));
            setSelectedAssetId(null);
            setConfirmDeleteId(null);
        } catch (err) {
            console.error('[MyContent] Delete failed:', err);
            dismiss('Delete failed', 'error');
        } finally {
            setDeleting(false);
        }
    }, [selectedTeamId, token]);

    const requestOpen = useCallback((asset: AssetItem) => {
        if (sceneHasContent()) {
            setConfirmingOpen(true);
        } else {
            setBusyAction('open');
            void doOpenAsset(asset, setInsertingAssetId, selectedTeamId ?? undefined).finally(() => setBusyAction(false));
        }
    }, [selectedTeamId]);

    // Drag/drop upload state
    const [isDragOver, setIsDragOver] = useState(false);
    const dropCounterRef = useRef(0);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Hydrate API client refs on every mount (refs are not cached in slots).
    useEffect(() => {
        teamsRef.current = new Teams(apiConfig(token));
        foldersRef.current = new Folders(apiConfig(token));
    }, [token]);

    // Fetch folders whenever the team changes (and on mount). When the
    // team-scoped cache has data it stays visible while the fetch refreshes
    // it in the background (stale-while-revalidate); the loading/error states
    // only show when there is nothing cached. Read the slots directly (not
    // the React state) because the team change re-hydrates state
    // asynchronously and the closure here may still be carrying the previous
    // team's data.
    useEffect(() => {
        if (!selectedTeamId) return;
        const hasCache = teamScopedSlot('myContent.folders', selectedTeamId, [] as FolderTeamResponseDto[]).length > 0;
        if (hasCache) setStatus('ready');
        else setStatus('loading');
        let cancelled = false;
        void (async () => {
            try {
                const res = await teamsRef.current!.foldersList(selectedTeamId);
                if (cancelled) return;
                const folderList = res.data?.folders ?? [];
                setFolders(folderList);
                setStatus('ready');

                const cachedFolder = teamScopedSlot('myContent.selectedFolder', selectedTeamId, null as FolderTeamResponseDto | null);
                if (cachedFolder?.folderId) {
                    // A folder is open from cache — refresh its assets in the
                    // background too so the visible grid isn't stale.
                    try {
                        const assetsRes = await foldersRef.current!.assetsList(cachedFolder.folderId);
                        if (!cancelled) setAssets((assetsRes.data ?? []).map(toAssetItem));
                    } catch (err) {
                        console.error('[MyContent] Refresh open folder assets failed:', err);
                    }
                    return;
                }

                const restoreId = getCatalogSelectedFolderId();
                if (restoreId) {
                    const match = folderList.find(f => f.folderId === restoreId);
                    if (match) {
                        setSelectedFolder_(match);
                        setLoadingAssets(true);
                        try {
                            const assetsRes = await foldersRef.current!.assetsList(match.folderId!);
                            if (!cancelled) setAssets((assetsRes.data ?? []).map(toAssetItem));
                        } catch {
                            if (!cancelled) setAssets([]);
                        } finally {
                            if (!cancelled) setLoadingAssets(false);
                        }
                    }
                }
            } catch (err) {
                console.error('[MyContent] Failed to fetch folders:', err);
                if (!cancelled && !hasCache) {
                    setFolders([]);
                    setErrorMsg(err instanceof Error ? err.message : 'Failed to load folders');
                    setStatus('error');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [selectedTeamId]);

    const refreshFolderList = useCallback(async () => {
        if (!selectedTeamId || !teamsRef.current) return;
        try {
            const res = await teamsRef.current.foldersList(selectedTeamId);
            setFolders(res.data?.folders ?? []);
        } catch (err) {
            console.error('[MyContent] Refresh folders failed:', err);
        }
    }, [selectedTeamId]);

    // NOTE: also called by refresh paths (refresh button, post-upload,
    // folder-refresh event) — must not reset UI state like assetSearch;
    // the folder-card click site clears that.
    const openFolder = useCallback(async (folder: FolderTeamResponseDto) => {
        if (!foldersRef.current || !folder.folderId) return;
        setSelectedFolder(folder);
        setLoadingAssets(true);
        try {
            const res = await foldersRef.current.assetsList(folder.folderId);
            setAssets((res.data ?? []).map(toAssetItem));
        } catch (err) {
            console.error('[MyContent] Failed to fetch assets:', err);
            setAssets([]);
        } finally {
            setLoadingAssets(false);
        }
    }, [setSelectedFolder]);

    const refreshCurrent = useCallback(async () => {
        if (selectedFolder) await openFolder(selectedFolder);
        else await refreshFolderList();
    }, [selectedFolder, openFolder, refreshFolderList]);
    useCatalogRefresh(active, refreshCurrent);

    const createFolder = useCallback(async () => {
        if (!selectedTeamId || !foldersRef.current) return;
        const name = prompt('Folder name:');
        if (!name?.trim()) return;
        const dismiss = showSpinnerToast(`Creating folder "${name}"...`);
        try {
            const res = await foldersRef.current.foldersCreate({ teamId: selectedTeamId, title: name.trim() });
            dismiss(`Created "${name}"`, 'success');
            // Refresh folder list
            const foldersRes = await teamsRef.current!.foldersList(selectedTeamId);
            setFolders(foldersRes.data?.folders ?? []);
            // Open the newly created folder
            const created = res.data;
            if (created?.folderId) {
                void openFolder({ folderId: created.folderId, title: created.title });
            }
        } catch (err) {
            console.error('[MyContent] Create folder failed:', err);
            dismiss('Failed to create folder', 'error');
        }
    }, [selectedTeamId, openFolder]);

    // Drag/drop upload: reset state when drag leaves the window entirely
    useEffect(() => {
        const reset = () => { dropCounterRef.current = 0; setIsDragOver(false); };
        document.addEventListener('drop', reset);
        document.addEventListener('dragend', reset);
        return () => {
            document.removeEventListener('drop', reset);
            document.removeEventListener('dragend', reset);
        };
    }, []);

    // Refresh folder contents when an external action (e.g. "Add to Catalog") uploads an asset
    useEffect(() => {
        const refresh = () => {
            if (selectedFolder) void openFolder(selectedFolder);
        };
        window.addEventListener('trace-catalog:folder-refresh', refresh);
        return () => { window.removeEventListener('trace-catalog:folder-refresh', refresh); };
    }, [selectedFolder, openFolder]);

    /** Upload files into the open folder. Shared by drag-drop and the Upload button. */
    const uploadFiles = useCallback(async (allFiles: File[]) => {
        if (!selectedFolder?.folderId || !selectedTeamId || uploading) return;

        const validFiles = allFiles.filter(file => {
            const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
            return UPLOAD_EXTENSIONS.includes(ext);
        });

        if (validFiles.length === 0) {
            showToast(`No supported files. Accepted: ${UPLOAD_EXTENSIONS.map(e => e.replace('.', '').toUpperCase()).join(', ')}`, 'error');
            return;
        }

        setUploading(true);
        const assetsClient = new Assets(apiConfig(token));
        let successCount = 0;
        let failCount = 0;

        const dismiss = showSpinnerToast(
            validFiles.length === 1
                ? `Uploading "${validFiles[0].name}"...`
                : `Uploading ${validFiles.length} files...`
        );

        for (const file of validFiles) {
            try {
                const entityType = extensionToEntityType(file.name);
                const title = file.name.replace(/\.[^/.]+$/, '');
                const viewer = document.querySelector('trace-viewer');
                const importer: ThumbnailImporter | undefined = viewer?.import && viewer.traceScene ? {
                    import: (blob: Blob, fileName: string, name?: string) => viewer.import(blob, fileName, name),
                    deleteSceneObject: (so) => viewer.traceScene?.deleteSceneObject(so),
                } : undefined;
                const thumbnail = importer && viewer?.scene
                    ? await generateAssetThumbnail(file, importer, viewer.scene)
                    : undefined;
                // The row's preview is the ONLY thing this produces, and it is
                // optional all the way to the database — so a capture that
                // comes back empty uploads a thumbnail-less row and says
                // nothing. Name which half gave up: no viewer to import into,
                // or an import/capture that did (it logs its own reason).
                if (!thumbnail) {
                    console.warn(
                        `[MyContent] No thumbnail for "${file.name}" — `
                        + (importer && viewer?.scene ? 'capture produced none' : 'no viewer to render one'),
                    );
                }
                // A `.glz` is written like any save, not posted whole: see
                // publishBundleAsAssetFolder (null in a legacy build or for a
                // legacy bundle, which keep the raw upload).
                const created = file.name.toLowerCase().endsWith('.glz')
                    ? await publishBundleAsAssetFolder(assetsClient, file, {
                        target: { folderId: selectedFolder.folderId, teamId: selectedTeamId },
                        title,
                        entityType: entityType ?? EntityType.Trace,
                        ...(thumbnail ? { thumbnail } : {}),
                    })
                    : null;
                if (created) {
                    successCount++;
                    continue;
                }
                // An arbitrary dropped/picked file: a leaf, a legacy bundle, or a
                // `.gltf` document. `withContentStorage` declares the build's
                // mode for the first two and forces `Folder` for the third.
                await assetsClient.assetsCreate(withContentStorage({
                    FolderId: selectedFolder.folderId,
                    TeamId: selectedTeamId,
                    File: file,
                    Thumbnail: thumbnail,
                    Title: title,
                    EntityType: entityType ?? EntityType.None,
                }));
                successCount++;
            } catch (err) {
                console.error(`[MyContent] Upload failed for ${file.name}:`, err);
                failCount++;
            }
        }

        if (failCount === 0) {
            dismiss(
                successCount === 1 ? `Uploaded "${validFiles[0].name}"` : `Uploaded ${successCount} files`,
                'success',
            );
        } else {
            dismiss(
                `${successCount} uploaded, ${failCount} failed`,
                failCount === validFiles.length ? 'error' : 'info',
            );
        }

        void openFolder(selectedFolder);
        setUploading(false);
    }, [selectedFolder, selectedTeamId, token, uploading, openFolder]);

    const onFolderDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dropCounterRef.current++;
        if (e.dataTransfer?.types.includes('Files')) {
            setIsDragOver(true);
        }
    }, []);

    const onFolderDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }, []);

    const onFolderDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dropCounterRef.current--;
        if (dropCounterRef.current <= 0) {
            dropCounterRef.current = 0;
            setIsDragOver(false);
        }
    }, []);

    const onFolderDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dropCounterRef.current = 0;
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer?.files ?? []);
        if (files.length) void uploadFiles(files);
    }, [uploadFiles]);

    const onFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        // Reset so picking the same file again re-triggers change.
        e.target.value = '';
        if (files.length) void uploadFiles(files);
    }, [uploadFiles]);

    // Publish dialog state
    const [publishOpen, setPublishOpen] = useState(false);
    const [publishName, setPublishName] = useState('');
    const [publishing, setPublishing] = useState(false);
    const previewRef = useRef<HTMLCanvasElement | null>(null);
    const [previewHeight, setPreviewHeight] = useState(200);
    const dragRef = useRef<{ startY: number; startH: number } | null>(null);

    // Manage persistent RTT that renders every frame while publish dialog is open
    useEffect(() => {
        if (!publishOpen) return;

        const viewer = document.querySelector('trace-viewer');
        const scene = viewer?.scene;
        const cam = scene?.activeCamera;
        if (!scene || !(cam instanceof ArcRotateCamera)) return;

        const dispose = createPublishPreview(scene, cam, (pixels, size) => {
            const canvas = previewRef.current;
            if (canvas) drawPixelsToCanvas(pixels, size, canvas);
        });

        return dispose ?? undefined;
    }, [publishOpen]);

    const startPublish = useCallback(() => {
        const viewer = document.querySelector('trace-viewer');
        // The document names itself: its root node is the file's own node
        // (SCENE_ROOT_SPEC D8), so the publish is named for it.
        const name = documentRoot(viewer?.traceScene)?.name
            ?? viewer?.traceScene?.sceneName
            ?? 'scene';
        setPublishName(name);
        setPublishOpen(true);
        setSelectedAssetId(null);
        setConfirmDeleteId(null);
        setConfirmingOpen(false);
    }, []);

    const confirmPublish = useCallback(async () => {
        if (!selectedFolder?.folderId || !selectedTeamId || !publishName.trim()) return;

        const viewer = document.querySelector('trace-viewer');
        if (!viewer?.exportToBlob) return;

        setPublishing(true);
        const dismiss = showSpinnerToast(`Publishing "${publishName}"...`);
        try {
            // A document that was never named carries the placeholder root
            // name; this publish gives it one, so the file's own root takes the
            // title before anything is exported — a placement of this row then
            // reads `Title (1) → Title → [content]` (SCENE_ROOT_SPEC D8). An
            // authored name is never rewritten.
            renameDefaultDocumentRoot(viewer.traceScene, publishName.trim());

            const scene = viewer.scene;
            const cam = scene?.activeCamera;
            const thumbnail = scene && cam instanceof ArcRotateCamera
                ? await captureSceneThumbnail(scene, cam)
                : undefined;

            const target = { folderId: selectedFolder.folderId, teamId: selectedTeamId };
            const assetsClient = new Assets(apiConfig(token));

            if (IS_FOLDER_STORAGE) {
                // The content model: the row is an asset FOLDER, and a folder
                // is written as a delta — the empty row, then each new file,
                // then the document naming what came back (§2.2, §2.6). The
                // server mints every storage name and validates the document
                // against them as uploaded, so nothing here predicts a name it
                // does not own. No `Metadata` either: root behaviors live in
                // the document's `scenes[0].extras.trace` (§3.6).
                //
                // `destination: 'other'` is what makes this a copy: the new
                // folder holds none of the open scene's files, so each one is
                // fetched and travels as bytes (§3.5). With the default
                // `'same'` they would come back on `folder.existing`, nothing
                // would be uploaded, and the published document would name
                // files its folder does not hold.
                const folder = await viewer.exportSceneFolder({ destination: 'other', ...editorExclusion(viewer.traceScene) });
                await publishNewAssetFolder(assetsClient, folder, {
                    target,
                    title: publishName,
                    // The row is a whole scene, not a reusable component (which
                    // is what a promotion produces) — unchanged from the legacy
                    // publish: only the shape of the write changes here, never
                    // what the row IS.
                    entityType: EntityType.Trace,
                    ...(thumbnail ? { thumbnail } : {}),
                });
            } else {
                const sceneBlob = await viewer.exportToBlob({ zipFormat: 'trace', ...editorExclusion(viewer.traceScene) });
                const file = new File([sceneBlob], `${publishName}.trace`, { type: 'application/octet-stream' });

                // Serialize the DOCUMENT ROOT's behaviors as asset metadata —
                // the file's own root data (SCENE_ROOT_SPEC D8, §5.1).
                const metadata = legacyRootMetadata(viewer.traceScene);

                // A `.trace` bundle with root behaviors in `Metadata` — the
                // legacy shape end to end, for the environments whose API has
                // no content-model endpoints at all (spec §6.1).
                await assetsClient.assetsCreate(withContentStorage({
                    FolderId: target.folderId,
                    TeamId: target.teamId,
                    File: file,
                    Thumbnail: thumbnail,
                    Title: publishName,
                    EntityType: EntityType.Trace,
                    Metadata: metadata,
                }));
            }

            setPublishOpen(false);
            dismiss(`Published "${publishName}"`, 'success');

            // Refresh folder contents
            void openFolder(selectedFolder);
        } catch (err) {
            console.error('[MyContent] Publish failed:', err);
            // The server's own words. A folder write is refused with a reason
            // — the offending `files[]` entry and why (spec §2.5) — and
            // swallowing it leaves a bare "400 Bad Request" that says nothing
            // about which reference to fix. `publishAsset` in `index.ts` logs
            // the same way.
            const responseData = (err as Error & { response?: { data?: unknown } })?.response?.data;
            if (responseData) console.error('[MyContent] API response:', responseData);
            const mapping = nameMismatchDetail(err);
            if (mapping) console.error(`[MyContent] ${mapping}`);
            dismiss(`Publish failed: ${publishErrorMessage(err)}`, 'error');
        } finally {
            setPublishing(false);
        }
    }, [publishName, selectedFolder, selectedTeamId, token, openFolder]);

    const cancelPublish = useCallback(() => {
        setPublishOpen(false);
    }, []);

    const selectedAsset = useMemo(
        () => selectedAssetId ? assets.find(a => a.assetId === selectedAssetId) ?? null : null,
        [selectedAssetId, assets],
    );

    const folderQuery = search.toLowerCase().trim();
    const assetQuery = assetSearch.toLowerCase().trim();

    const filteredFolders = useMemo(() => {
        const searched = folderQuery ? folders.filter(f => f.title?.toLowerCase().includes(folderQuery)) : folders;
        // Folders have no dateModified — "updated" falls back to created.
        return sortBy(searched, sortKey, {
            name: f => f.title,
            created: f => f.dateCreated,
            modified: () => undefined,
        });
    }, [folders, folderQuery, sortKey]);

    const filteredAssets = useMemo(() => {
        const searched = assetQuery
            ? assets.filter(a =>
                (a.title?.toLowerCase().includes(assetQuery) ?? false) ||
                (a.originalFilename?.toLowerCase().includes(assetQuery) ?? false))
            : assets;
        return sortBy(searched, sortKey, {
            name: a => a.title ?? a.originalFilename,
            created: a => a.dateCreated,
            modified: a => a.dateModified,
        });
    }, [assets, assetQuery, sortKey]);

    if (!selectedTeamId) {
        return <div style={styles.center}>No team selected</div>;
    }

    if (status === 'loading') {
        return <div style={styles.center}>Loading folders...</div>;
    }

    if (status === 'error') {
        return (
            <div style={styles.center}>
                <div>{errorMsg || 'Failed to load folders'}</div>
            </div>
        );
    }

    if (selectedFolder) {
        return (
            <>
                <div style={styles.header}>
                    <button style={styles.backButton} onClick={() => { setSelectedFolder(null); setAssets([]); setPublishOpen(false); setSelectedAssetId(null); setConfirmDeleteId(null); setConfirmingOpen(false); }} title="Back to folders">&#8592;</button>
                    <div style={{ ...styles.title, flex: 1, minWidth: 0 }}>{selectedFolder.title ?? 'Folder'}</div>
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        title="Upload files to this folder"
                        style={{
                            padding: '3px 10px', fontSize: '11px', fontWeight: 600,
                            border: '1px solid var(--colorNeutralStroke1)', borderRadius: '3px',
                            cursor: uploading ? 'default' : 'pointer',
                            backgroundColor: 'var(--colorNeutralBackground3)', color: 'var(--colorNeutralForeground1)',
                            opacity: uploading ? 0.6 : 1,
                            display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M2.75 14A1.75 1.75 0 011 12.25v-2.5a.75.75 0 011.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 00.25-.25v-2.5a.75.75 0 011.5 0v2.5A1.75 1.75 0 0113.25 14H2.75z"/>
                            <path d="M7.25 4.061V9.75a.75.75 0 001.5 0V4.061l1.97 1.969a.749.749 0 101.06-1.06L8.53 1.72a.749.749 0 00-1.06 0L4.22 4.97a.749.749 0 101.06 1.06l1.97-1.969z"/>
                        </svg>
                        {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={UPLOAD_EXTENSIONS.join(',')}
                        onChange={onFilePick}
                        style={{ display: 'none' }}
                    />
                </div>

                <input type="text" placeholder="Search assets..." value={assetSearch} onChange={e => setAssetSearch(e.target.value)} style={styles.searchInput} />
                <FilterSortBar sort={sortKey} onSortChange={setSortKey} />
                {selectedAsset && (
                    <>
                        <AssetInfoBar
                            asset={selectedAsset}
                            onImport={() => { setBusyAction('import'); void doImportAsset(selectedAsset, setInsertingAssetId).finally(() => setBusyAction(false)); }}
                            onOpen={() => requestOpen(selectedAsset)}
                            onDeselect={() => { setSelectedAssetId(null); setConfirmDeleteId(null); setConfirmingOpen(false); }}
                            busy={insertingAssetId === selectedAsset.assetId ? busyAction : false}
                            onDelete={() => setConfirmDeleteId(selectedAsset.assetId ?? null)}
                            deleting={deleting}
                        />
                        {confirmingOpen && (
                            <ConfirmOpenBar
                                name={selectedAsset.title ?? selectedAsset.originalFilename ?? 'asset'}
                                onConfirm={() => { setConfirmingOpen(false); setBusyAction('open'); void doOpenAsset(selectedAsset, setInsertingAssetId, selectedTeamId ?? undefined).finally(() => setBusyAction(false)); }}
                                onCancel={() => setConfirmingOpen(false)}
                            />
                        )}
                        {confirmDeleteId === selectedAsset.assetId && (
                            <ConfirmDeleteBar
                                name={selectedAsset.title ?? 'asset'}
                                busy={deleting}
                                onConfirm={() => { void handleDeleteAsset(selectedAsset); }}
                                onCancel={() => setConfirmDeleteId(null)}
                            />
                        )}
                    </>
                )}
                {/* Drop zone for file upload */}
                <div
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0 }}
                    onDragEnter={onFolderDragEnter}
                    onDragOver={onFolderDragOver}
                    onDragLeave={onFolderDragLeave}
                    onDrop={onFolderDrop}
                >
                {isDragOver && (
                    <div style={{
                        position: 'absolute', inset: 0, border: '2px dashed #4CAF50', borderRadius: '4px',
                        backgroundColor: 'rgba(76, 175, 80, 0.08)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', zIndex: 10, pointerEvents: 'none',
                    }}>
                        <div style={{
                            color: '#4CAF50', fontSize: '12px', fontWeight: 600, padding: '8px 16px',
                            backgroundColor: 'var(--colorNeutralBackground1)', borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }}>
                            Drop files to upload
                        </div>
                    </div>
                )}
                {loadingAssets ? (
                    <div style={styles.center}>Loading assets...</div>
                ) : (
                    <div style={styles.flexGrid}>
                        {publishOpen ? (
                            <div style={{ width: '100%', padding: '8px', backgroundColor: 'var(--colorNeutralBackground2)', borderRadius: '4px', marginBottom: '2px' }}>
                                <div style={{ width: '100%', height: previewHeight, overflow: 'hidden', borderRadius: '3px', marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <canvas
                                        ref={previewRef}
                                        width={512}
                                        height={512}
                                        style={{ width: previewHeight, height: previewHeight, display: 'block', borderRadius: '3px', background: 'repeating-conic-gradient(var(--colorNeutralBackground3) 0% 25%, var(--colorNeutralBackground2) 0% 50%) 50% / 16px 16px' }}
                                    />
                                </div>
                                {/* Resize handle */}
                                <div
                                    style={{ width: '100%', height: '8px', cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        dragRef.current = { startY: e.clientY, startH: previewHeight };
                                        const onMove = (ev: MouseEvent) => {
                                            if (!dragRef.current) return;
                                            const delta = ev.clientY - dragRef.current.startY;
                                            setPreviewHeight(Math.max(80, Math.min(500, dragRef.current.startH + delta)));
                                        };
                                        const onUp = () => {
                                            dragRef.current = null;
                                            window.removeEventListener('mousemove', onMove);
                                            window.removeEventListener('mouseup', onUp);
                                        };
                                        window.addEventListener('mousemove', onMove);
                                        window.addEventListener('mouseup', onUp);
                                    }}
                                >
                                    <div style={{ width: '32px', height: '3px', borderRadius: '2px', backgroundColor: 'var(--colorNeutralStroke1)' }} />
                                </div>
                                <input
                                    type="text"
                                    value={publishName}
                                    onChange={e => setPublishName(e.target.value)}
                                    style={{ ...styles.input, width: '100%', marginBottom: '6px' }}
                                    placeholder="Asset name"
                                    autoFocus
                                />
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        style={{ ...styles.loginButton, flex: 1, opacity: publishing || !publishName.trim() ? 0.5 : 1 }}
                                        onClick={confirmPublish}
                                        disabled={publishing || !publishName.trim()}
                                    >
                                        {publishing ? 'Publishing...' : 'Publish'}
                                    </button>
                                    <button
                                        style={{ ...styles.loginButton, flex: 1, backgroundColor: 'var(--colorNeutralBackground3)', color: 'var(--colorNeutralForeground1)', opacity: publishing ? 0.5 : 1 }}
                                        onClick={cancelPublish}
                                        disabled={publishing}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <PublishCard onClick={startPublish} />
                        )}
                        {filteredAssets.map(asset => (
                            <AssetCard
                                key={asset.assetId}
                                asset={asset}
                                onSelect={() => { setSelectedAssetId(asset.assetId ?? null); setPublishOpen(false); }}
                                onImport={() => doImportAsset(asset, setInsertingAssetId)}
                                selected={asset.assetId === selectedAssetId}
                                inserting={insertingAssetId === asset.assetId}
                            />
                        ))}
                        <Spacers />
                    </div>
                )}
                </div>
            </>
        );
    }

    return (
        <>
            <input type="text" placeholder="Search folders..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
            <FilterSortBar sort={sortKey} onSortChange={setSortKey} />
            {filteredFolders.length === 0 && folderQuery ? (
                <div style={styles.center}>No matching folders</div>
            ) : (
                <div style={styles.flexGrid}>
                    {!folderQuery && <NewFolderCard onClick={createFolder} />}
                    {filteredFolders.map(folder => (
                        <FolderCard key={folder.folderId} folder={folder} onClick={() => { setAssetSearch(''); void openFolder(folder); }} />
                    ))}
                    <Spacers />
                </div>
            )}
        </>
    );
};
